import React, { useState, useEffect, useCallback } from "react";
import { ChevronRight, TrendingUp, TrendingDown, X, Settings2, CheckCircle, AlertCircle, AlertTriangle, Layers, Activity } from "lucide-react";

// Effective analyst hours per FTE per month — mirrors the backend EffHoursPerFtePerMonth (109).
const EFFECTIVE_HOURS_PER_MONTH = 109;
const fmt  = (n) => n == null ? "—" : Number(n).toLocaleString("en-US", { maximumFractionDigits: 1 });
// Show extra precision when two FTE values are close — prevents "2.6 = 2.6 but gap exists" confusion
const fmtFte = (val, other) => {
  if (val == null) return "—";
  const diff = other != null ? Math.abs(val - other) : 1;
  return diff < 0.1 ? Number(val).toFixed(2) : fmt(val);
};
const fmtM = (n) => n == null ? "—" : n >= 1_000_000
  ? `$${(n / 1_000_000).toFixed(1)}M`
  : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}K` : `$${n}`;

const CONF = {
  HIGH:   { text: "text-emerald-700", bg: "bg-emerald-50", dot: "bg-emerald-500", label: "High" },
  MEDIUM: { text: "text-amber-700",   bg: "bg-amber-50",   dot: "bg-amber-500",   label: "Med"  },
  LOW:    { text: "text-gray-500",    bg: "bg-gray-50",    dot: "bg-gray-400",    label: "Low"  },
};

function CoveragePill({ pct }) {
  if (pct == null) return <span className="text-xs text-gray-400">—</span>;
  const color     = pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500";
  const textColor = pct >= 80 ? "text-emerald-700" : pct >= 50 ? "text-amber-700" : "text-red-700";
  const filled    = Math.min(100, pct);
  return (
    <div className="min-w-[90px]">
      <div className="text-center mb-0.5">
        <span className={`text-xs font-bold tabular-nums ${textColor}`}>{Math.round(pct)}%</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${filled}%` }} />
      </div>
    </div>
  );
}

function GapBadge({ gap }) {
  if (gap == null) return null;
  if (gap <= 0) return (
    <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-emerald-700">
      <TrendingDown className="w-3 h-3" />
      Covered
    </span>
  );
  return (
    <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-red-700">
      <TrendingUp className="w-3 h-3" />
      +{fmt(gap)} FTE
    </span>
  );
}

// Utilization status badge — reflects backend-computed UtilizationPercent
const UTIL_STATUS = {
  Critical:      { bg: "bg-red-100",     text: "text-red-700",     dot: "bg-red-500",     label: "Critical"     },
  Healthy:       { bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-500", label: "Healthy"      },
  Overstaffed:   { bg: "bg-sky-100",     text: "text-sky-700",     dot: "bg-sky-500",     label: "Overstaffed"  },
  Unconfigured:  { bg: "bg-gray-100",    text: "text-gray-400",    dot: "bg-gray-300",    label: "—"            },
};

function UtilizationBadge({ status, pct }) {
  const cfg = UTIL_STATUS[status] || UTIL_STATUS.Unconfigured;
  const showPct = status && status !== "Unconfigured" && pct > 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}
      title={showPct ? `Utilization: ${pct.toFixed(0)}% (Required ÷ Available)` : "Team headcount not configured"}>
      <span className={`w-1 h-1 rounded-full ${cfg.dot}`} />
      {showPct ? `${Math.round(pct)}%` : cfg.label}
    </span>
  );
}

// ── Calibrate tab field definitions ──────────────────────────────────────────
const OVERRIDE_FIELDS = [
  { key: "BaseHours",          label: "Base Hours",             unit: "h",  hint: "Hours per finding regardless of scale" },
  { key: "CriticalMultiplier", label: "Critical Multiplier",    unit: "×",  hint: "Effort multiplier for Critical severity" },
  { key: "HighMultiplier",     label: "High Multiplier",        unit: "×",  hint: "Effort multiplier for High severity" },
  { key: "MediumMultiplier",   label: "Medium Multiplier",      unit: "×",  hint: "Effort multiplier for Medium severity" },
  { key: "LowMultiplier",      label: "Low Multiplier",         unit: "×",  hint: "Effort multiplier for Low severity" },
  { key: "PerAssetHours",      label: "Per-Asset Hours",        unit: "h",  hint: "Additional hours per affected asset" },
  { key: "PerUserHours",       label: "Per-User Hours",         unit: "h",  hint: "Additional hours per affected user" },
  { key: "PerVendorHours",     label: "Per-Vendor Hours",       unit: "h",  hint: "Additional hours per affected vendor" },
];

function CalibrateTab({ skill, apiCall, onOverrideSaved }) {
  const [data,        setData]        = useState(null);   // WieOverridesResponseDto
  const [loading,     setLoading]     = useState(false);
  const [inputs,      setInputs]      = useState({});     // { fieldName: { value, justification } }
  const [saving,      setSaving]      = useState({});     // { fieldName: bool }
  const [saved,       setSaved]       = useState({});     // { fieldName: bool } — flash feedback

  const load = useCallback(async () => {
    if (!apiCall) return;
    setLoading(true);
    try {
      const res = await apiCall("GET", `/workforce/overrides?skillArea=${skill.skillArea}`);
      setData(res);
      // Pre-fill inputs with current effective values
      const init = {};
      OVERRIDE_FIELDS.forEach(f => {
        init[f.key] = { value: String(res.defaults?.[lcFirst(f.key)] ?? ""), justification: "" };
      });
      setInputs(init);
    } catch { /* silently ignore — table still renders */ }
    finally { setLoading(false); }
  }, [apiCall, skill.skillArea]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (fieldKey) => {
    if (!apiCall) return;
    const raw = inputs[fieldKey]?.value;
    const val = parseFloat(raw);
    // Allow explicit 0 — only reject NaN (empty/non-numeric) or negative values
    if (isNaN(val) || val < 0) return;
    setSaving(prev => ({ ...prev, [fieldKey]: true }));
    try {
      await apiCall("POST", "/workforce/overrides", {
        skillArea:     skill.skillArea,
        fieldName:     fieldKey,
        overrideValue: val,
        justification: inputs[fieldKey]?.justification || null,
      });
      setSaved(prev => ({ ...prev, [fieldKey]: true }));
      setTimeout(() => setSaved(prev => ({ ...prev, [fieldKey]: false })), 2000);
      await load();              // refresh this tab's audit log + effective values
      await onOverrideSaved?.(); // re-run the forecast so headline FTE/skill numbers update immediately
    } catch { /* show nothing — keep the save button available */ }
    finally { setSaving(prev => ({ ...prev, [fieldKey]: false })); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-8 text-xs text-gray-400">
      Loading calibration data…
    </div>
  );

  const effectiveValues = data?.defaults ?? {};
  const auditLog        = data?.auditLog ?? [];

  return (
    <div className="space-y-4">
      {/* Field overrides table */}
      <div>
        <div className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
          Effort Model Parameters — {skill.skillLabel}
        </div>
        <div className="space-y-2">
          {OVERRIDE_FIELDS.map(f => {
            const effectiveVal = effectiveValues[lcFirst(f.key)];
            const fieldAudit   = auditLog.filter(e => e.fieldName === f.key);
            const hasOverride  = fieldAudit.length > 0;
            return (
              <div key={f.key} className={`rounded-xl border px-4 py-3 ${hasOverride ? "border-purple-200 bg-purple-50" : "border-gray-200 bg-white"}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-xs font-semibold text-gray-800">{f.label}</span>
                      {hasOverride && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                          Overridden
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400">{f.hint}</div>
                    {effectiveVal != null && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        Current: <span className="font-semibold text-gray-700">{effectiveVal}{f.unit}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <input
                      type="number" min="0" step="0.05"
                      value={inputs[f.key]?.value ?? ""}
                      onChange={e => setInputs(prev => ({
                        ...prev, [f.key]: { ...prev[f.key], value: e.target.value }
                      }))}
                      placeholder={String(effectiveVal ?? "")}
                      className="w-20 border border-gray-200 rounded-lg px-2 py-1 text-xs text-center focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400"
                    />
                    <span className="text-xs text-gray-400">{f.unit}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="text"
                    value={inputs[f.key]?.justification ?? ""}
                    onChange={e => setInputs(prev => ({
                      ...prev, [f.key]: { ...prev[f.key], justification: e.target.value }
                    }))}
                    placeholder="Justification (optional)"
                    className="flex-1 border border-gray-200 rounded-lg px-2.5 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400"
                  />
                  <button
                    onClick={() => handleSave(f.key)}
                    disabled={saving[f.key] || inputs[f.key]?.value === "" || inputs[f.key]?.value == null}
                    className={`flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg transition-colors disabled:opacity-40
                      ${saved[f.key]
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-purple-100 text-purple-700 hover:bg-purple-200"}`}
                  >
                    {saved[f.key]
                      ? <><CheckCircle className="w-3 h-3" /> Saved</>
                      : saving[f.key] ? "Saving…" : "Apply"
                    }
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Overrides apply to ALL findings in this skill area. Applying recalculates the forecast automatically.
        </p>
      </div>

      {/* Audit log */}
      {auditLog.length > 0 && (
        <div>
          <div className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">
            Override Audit Log
          </div>
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left font-semibold text-gray-500 px-3 py-2">Field</th>
                  <th className="text-right font-semibold text-gray-500 px-3 py-2">Was</th>
                  <th className="text-right font-semibold text-gray-500 px-3 py-2">Set To</th>
                  <th className="text-left font-semibold text-gray-500 px-3 py-2">By</th>
                  <th className="text-left font-semibold text-gray-500 px-3 py-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {auditLog.map(e => (
                  <tr key={e.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-3 py-2 font-medium text-gray-800">
                      {OVERRIDE_FIELDS.find(f => f.key === e.fieldName)?.label ?? e.fieldName}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-500">{e.originalValue}</td>
                    <td className="px-3 py-2 text-right font-semibold text-purple-700">{e.overrideValue}</td>
                    <td className="px-3 py-2 text-gray-500 truncate max-w-[100px]" title={e.changedBy ?? "—"}>{e.changedBy ?? "—"}</td>
                    <td className="px-3 py-2 text-gray-400 whitespace-nowrap">
                      {new Date(e.changedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {auditLog[0]?.justification && (
            <p className="text-xs text-gray-400 mt-1 italic">
              Latest note: "{auditLog[0].justification}"
            </p>
          )}
        </div>
      )}

      {!auditLog.length && (
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
          <AlertCircle className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <p className="text-xs text-gray-500">No overrides applied yet. All values use industry benchmark defaults.</p>
        </div>
      )}
    </div>
  );
}

// camelCase helper — maps "BaseHours" → "baseHours" for EffortModelValuesDto field access
function lcFirst(s) { return s.charAt(0).toLowerCase() + s.slice(1); }

// Inline drilldown panel rendered as a table row or card
function DrilldownPanel({ skill, colSpan, onClose, horizonDays, apiCall, canManage, onOverrideSaved }) {
  const [activeTab, setActiveTab] = useState("detail");
  if (!skill) return null;

  // Calibrate (effort-model overrides) is RISK_MANAGER+@ORG only; backend enforces, this gates the UI.
  const showCalibrate = activeTab === "calibrate" && canManage;

  // Passed into the formula note so the Calibrate link actually switches tabs
  const goCalibrate = () => setActiveTab("calibrate");

  // One-time effort to fully clear this skill's backlog (person-months; raw hours in tooltip).
  const totalEffortHours = skill.totalEffortHours ?? 0;
  const totalEffortPm    = totalEffortHours / EFFECTIVE_HOURS_PER_MONTH;
  const avgEffortPerFinding = skill.findingCount > 0 && totalEffortHours > 0
    ? (totalEffortHours / skill.findingCount).toFixed(1)
    : null;
  // Time to clear at the configured team (only meaningful once capacity is set — i.e. Gap Analysis).
  const monthsToClear = skill.availableFte > 0 && totalEffortHours > 0
    ? totalEffortHours / (skill.availableFte * EFFECTIVE_HOURS_PER_MONTH)
    : null;

  const dedupLabel = skill.duplicatesRemoved > 0
    ? `${skill.findingCount} unique (+${skill.duplicatesRemoved} merged)`
    : (skill.findingCount ?? "—");

  const kpis = [
    { label: "Required FTE",          value: (skill.requiredFte ?? 0).toFixed(1) + " FTE",
      title: "Sustained team to clear this skill's backlog within the timeline = Σ(effort ÷ min(timeline, severity SLA)) ÷ 109. Urgent findings are capped to their SLA, so it can exceed effort ÷ timeline." },
    { label: "Total effort to clear", value: fmt(totalEffortPm) + " FTE-months",
      title: `${fmt(totalEffortHours)} h of one-time remediation effort to clear this backlog` },
    { label: "Unique Findings",       value: dedupLabel, title: skill.duplicatesRemoved > 0 ? `${skill.rawFindingCount} raw findings deduplicated to ${skill.findingCount} unique` : undefined },
    { label: "Avg effort / finding",  value: avgEffortPerFinding ? avgEffortPerFinding + " h" : "—", title: "Average one-time remediation effort per unique finding" },
    ...(monthsToClear != null ? [{
      label: "Time to clear",
      value: fmt(monthsToClear) + " mo",
      title: `At your configured team of ${fmt(skill.availableFte)} FTE`
    }] : []),
    { label: "Confidence",            value: skill.confidence ?? "—" },
  ];

  const panel = (
    <div className="bg-blue-50 border-t border-blue-100 px-5 py-4">
      {/* Panel header with tab switcher */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h4 className="text-sm font-bold text-gray-900">{skill.skillLabel}</h4>
          <div className="flex items-center gap-0.5 bg-white border border-gray-200 rounded-lg p-0.5">
            <button
              onClick={() => setActiveTab("detail")}
              className={`flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md transition-colors
                ${activeTab === "detail" ? "bg-blue-600 text-white" : "text-gray-500 hover:text-gray-700"}`}
            >
              Detail
            </button>
            {canManage && (
              <button
                onClick={() => setActiveTab("calibrate")}
                className={`flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md transition-colors
                  ${activeTab === "calibrate" ? "bg-purple-600 text-white" : "text-gray-500 hover:text-gray-700"}`}
              >
                <Settings2 className="w-3 h-3" />
                Calibrate
              </button>
            )}
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-blue-100 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Calibrate tab */}
      {showCalibrate && (
        <CalibrateTab skill={skill} apiCall={apiCall} onOverrideSaved={onOverrideSaved} />
      )}

      {/* Detail tab (also the fallback when a non-manager has no Calibrate access) */}
      {!showCalibrate && <>

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        {kpis.map(kpi => (
          <div key={kpi.label} className="rounded-xl bg-white border border-blue-100 px-3 py-2.5" title={kpi.title}>
            <div className="text-xs text-gray-500 mb-0.5">{kpi.label}</div>
            <div className="text-sm font-bold text-gray-900">{kpi.value}</div>
          </div>
        ))}
      </div>

      {/* Severity + Source breakdown */}
      {(skill.severityBreakdown?.total > 0 || skill.sourceBreakdown?.length > 0) && (
        <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {skill.severityBreakdown?.total > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
              <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Severity Breakdown</div>
              <div className="space-y-1">
                {[
                  { label: "Critical", count: skill.severityBreakdown.critical, barColor: "bg-red-500",    textColor: "text-red-700" },
                  { label: "High",     count: skill.severityBreakdown.high,     barColor: "bg-orange-400", textColor: "text-orange-700" },
                  { label: "Medium",   count: skill.severityBreakdown.medium,   barColor: "bg-amber-400",  textColor: "text-amber-700" },
                  { label: "Low",      count: skill.severityBreakdown.low,      barColor: "bg-gray-300",   textColor: "text-gray-500" },
                ].map(({ label, count, barColor, textColor }) => count > 0 && (
                  <div key={label} className="flex items-center gap-2">
                    <span className={`text-[10px] font-semibold w-12 flex-shrink-0 ${textColor}`}>{label}</span>
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${barColor}`}
                        style={{ width: `${(count / skill.severityBreakdown.total) * 100}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-gray-500 tabular-nums w-6 text-right">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {skill.sourceBreakdown?.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
              <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Evidence Sources</div>
              <div className="space-y-1">
                {skill.sourceBreakdown.map(s => (
                  <div key={s.source} className="flex items-center gap-2">
                    <span className="flex-1 text-[10px] text-gray-600 truncate" title={s.label}>{s.label}</span>
                    <span className="text-[10px] font-semibold text-blue-700 tabular-nums">{s.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* FTE formula transparency — audit requirement */}
      <div className="mb-4 bg-white border border-gray-200 rounded-xl px-4 py-2.5">
        <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">FTE Calculation Basis</div>
        <div className="font-mono text-[10px] text-gray-600 leading-relaxed">
          effort = BaseHours × SeverityMult × AMF × 1.10 × 1.15<br />
          Required FTE = Σ [ effort ÷ min(timeline, severity SLA) ] ÷ 109 h/month
        </div>
        <div className="text-[10px] text-gray-400 mt-1">
          SeverityMult: Critical 1.5× · High 1.25× · Medium 1.0× · Low 0.75× &nbsp;·&nbsp;
          Severity SLA: Critical 1mo · High 2mo · Medium 4mo · Low 8mo
          {canManage && <>&nbsp;·&nbsp;Adjust parameters in the <button onClick={goCalibrate} className="underline text-purple-600">Calibrate tab</button></>}
        </div>
      </div>

      {/* Financial Exposure — from monetary engine (IBM CODB × Verizon DBIR) */}
      {(skill.exposureLow > 0 || skill.exposureHigh > 0) && (
        <div className="mb-4">
          <div className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
            Financial Exposure
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div className="rounded-xl bg-white border border-orange-100 px-3 py-2.5">
              <div className="text-xs text-gray-500 mb-0.5">Lower Estimate</div>
              <div className="text-sm font-bold text-orange-600">{fmtM(skill.exposureLow)}</div>
            </div>
            <div className="rounded-xl bg-white border border-orange-200 px-3 py-2.5">
              <div className="text-xs text-gray-500 mb-0.5">Upper Estimate</div>
              <div className="text-sm font-bold text-red-600">{fmtM(skill.exposureHigh)}</div>
            </div>
            {skill.loadedAnnualCost > 0 && skill.gapFte > 0 && (
              <div className="rounded-xl bg-white border border-blue-100 px-3 py-2.5">
                <div className="text-xs text-gray-500 mb-0.5">Annual Gap Cost</div>
                <div className="text-sm font-bold text-blue-700">{fmtM(skill.gapFte * skill.loadedAnnualCost)}</div>
              </div>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-1.5">
            IBM CODB 2023 × Verizon DBIR 2023 breach probability — if findings unresolved
          </p>
        </div>
      )}

      {/* FTE Budget — shown when loaded annual cost is configured */}
      {skill.loadedAnnualCost > 0 && (
        <div className="mb-4">
          <div className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
            FTE Budget
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl bg-white border border-gray-200 px-3 py-2.5">
              <div className="text-xs text-gray-500 mb-0.5">Avg. Loaded Cost / FTE</div>
              <div className="text-sm font-bold text-gray-900">
                {fmtM(skill.loadedAnnualCost)}<span className="text-xs font-normal text-gray-400"> /yr</span>
              </div>
            </div>
            <div className="rounded-xl bg-white border border-gray-200 px-3 py-2.5">
              <div className="text-xs text-gray-500 mb-0.5">Required Team Budget</div>
              <div className="text-sm font-bold text-gray-900">
                {fmtM(skill.requiredFte * skill.loadedAnnualCost)}<span className="text-xs font-normal text-gray-400"> /yr</span>
              </div>
              <div className="text-[10px] text-gray-400 mt-0.5">
                {(skill.requiredFte ?? 0).toFixed(2)} FTE × {fmtM(skill.loadedAnnualCost)}/FTE
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confidence breakdown — always shown, uses backend confidenceDetail when available */}
      {(skill.confidence !== "HIGH" || skill.confidenceDetail) && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="text-xs font-bold text-gray-700 uppercase tracking-wider">
              Confidence Criteria
            </div>
            {skill.confidenceDetail && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                Score {skill.confidenceDetail.score}/3 ({Math.round((skill.confidenceDetail.score / 3) * 100)}%)
              </span>
            )}
            {skill.historicalValidationMet && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200"
                title="This skill area's forecast outputs have been compared against actual remediation data.">
                ✓ Validated
              </span>
            )}
            {skill.confidenceDetail?.dataCompleteness && (
              <span className="text-[10px] text-gray-400">— {skill.confidenceDetail.dataCompleteness}</span>
            )}
          </div>
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-3 space-y-2">
            {skill.confidenceDetail ? (
              <>
                {[
                  {
                    met: skill.confidenceDetail.hasSufficientFindings,
                    label: "Minimum data points",
                    detail: `${skill.confidenceDetail.sampleSize} findings — need ≥ 3 for basic estimates`,
                  },
                  {
                    met: skill.confidenceDetail.hasStatisticalSample,
                    label: "Statistically meaningful sample",
                    detail: "Need ≥ 8 findings for reliable FTE estimates",
                  },
                  {
                    met: skill.confidenceDetail.hasFteConfigured,
                    label: "Team headcount configured",
                    detail: "Go to Gap Analysis → Team Setup and enter current FTE",
                  },
                ].map(c => (
                  <div key={c.label} className="flex items-start gap-2.5">
                    <span className={`mt-0.5 flex-shrink-0 text-sm font-bold ${c.met ? "text-emerald-500" : "text-gray-300"}`}>
                      {c.met ? "✓" : "○"}
                    </span>
                    <div>
                      <span className={`text-xs font-semibold ${c.met ? "text-gray-700" : "text-gray-400"}`}>
                        {c.label}
                      </span>
                      {!c.met && (
                        <p className="text-xs text-amber-700 mt-0.5">{c.detail}</p>
                      )}
                    </div>
                  </div>
                ))}
                {skill.confidenceDetail.limitations?.length > 0 && (
                  <div className="pt-1 mt-1 border-t border-gray-100">
                    <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Limitations</div>
                    {skill.confidenceDetail.limitations.map((lim, i) => (
                      <p key={i} className="text-xs text-amber-700 flex items-start gap-1">
                        <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                        {lim}
                      </p>
                    ))}
                  </div>
                )}
              </>
            ) : (
              /* fallback to local heuristic if backend detail not present */
              ([
                {
                  met: skill.findingCount >= 3,
                  label: "Minimum data points",
                  detail: "At least 3 open findings in this skill area",
                },
                {
                  met: skill.findingCount >= 8,
                  label: "Statistically meaningful sample",
                  detail: "At least 8 open findings for reliable FTE estimates",
                },
                {
                  met: skill.availableHours > 0,
                  label: "Team headcount configured",
                  detail: skill.availableHours > 0
                    ? "Capacity data entered in Team Setup"
                    : "Go to Gap Analysis → Team Setup and enter current FTE for this skill area",
                },
              ].map(c => (
                <div key={c.label} className="flex items-start gap-2.5">
                  <span className={`mt-0.5 flex-shrink-0 text-sm font-bold ${c.met ? "text-emerald-500" : "text-gray-300"}`}>
                    {c.met ? "✓" : "○"}
                  </span>
                  <div>
                    <span className={`text-xs font-semibold ${c.met ? "text-gray-700" : "text-gray-400"}`}>
                      {c.label}
                    </span>
                    {!c.met && (
                      <p className="text-xs text-amber-700 mt-0.5">{c.detail}</p>
                    )}
                  </div>
                </div>
              )))
            )}
          </div>
        </div>
      )}

      {/* Phase 2A — AI Narrative */}
      {skill.aiNarrative && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">AI Workforce Narrative</span>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-600 border border-blue-200">AI-generated</span>
          </div>
          <div className="bg-white border border-blue-100 rounded-xl px-4 py-3">
            <p className="text-xs text-gray-700 leading-relaxed">{skill.aiNarrative}</p>
          </div>
          <p className="text-[10px] text-amber-700 mt-1.5 flex items-start gap-1">
            <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
            AI-generated summary of observed finding patterns. Workload contributors listed are the highest-hour items — not confirmed root causes.
            Do not treat this as a root cause analysis. Verify findings independently before acting.
          </p>
        </div>
      )}

      {/* Phase 2C — Minimum Stabilization FTE */}
      {skill.stabilizationFte > 0 && (
        <div className="mb-4">
          <div className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
            Minimum Stabilization Required
          </div>
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
            <span className="text-amber-500 mt-0.5 flex-shrink-0 text-sm">⚡</span>
            <p className="text-xs text-amber-800">
              Adding <span className="font-bold">{skill.stabilizationFte.toFixed(1)} FTE</span> closes this gap within the {horizonDays}-day timeline.
            </p>
          </div>
        </div>
      )}

      {/* Risk drivers */}
      {skill.topDrivers?.length > 0 ? (
        <>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Top Risk Drivers
          </div>
          <div className="space-y-1.5">
            {skill.topDrivers.map((d, i) => (
              <div key={i} className="text-xs bg-white rounded-lg px-3 py-2 border border-blue-100">
                <div className="flex items-center gap-3">
                  <span className="w-5 h-5 rounded-full bg-gray-100 text-gray-500 font-bold flex items-center justify-center flex-shrink-0 text-[10px]">
                    {i + 1}
                  </span>
                  <span className="flex-1 text-gray-800 truncate" title={d.riskTitle || "Untitled risk"}>
                    {d.riskTitle || <span className="text-gray-400 italic">Untitled risk</span>}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded-full font-medium flex-shrink-0
                    ${d.severity === "Critical" ? "bg-red-100 text-red-700"
                    : d.severity === "High"     ? "bg-orange-100 text-orange-700"
                    : d.severity === "Medium"   ? "bg-amber-100 text-amber-700"
                    :                             "bg-gray-100 text-gray-500"}`}>
                    {d.severity}
                  </span>
                  <span className="font-semibold text-blue-700 flex-shrink-0 tabular-nums">
                    {fmt(d.hours)}h
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1 ml-8">
                  {d.source && (
                    <span className="text-[10px] text-gray-400 truncate">{d.source}</span>
                  )}
                  {d.duplicateCount > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1 py-0.5 rounded bg-purple-50 text-purple-600 flex-shrink-0">
                      <Layers className="w-2.5 h-2.5" />
                      +{d.duplicateCount} similar
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="text-xs text-gray-400">No driver detail available for this skill area.</p>
      )}

      </> /* end detail tab */}
    </div>
  );

  // Desktop: wrap in a table row
  if (colSpan) {
    return (
      <tr>
        <td colSpan={colSpan} className="p-0">
          {panel}
        </td>
      </tr>
    );
  }

  // Mobile: plain div
  return panel;
}

// ── Low-volume summary row ────────────────────────────────────────────────────
function LowVolumeRow({ skills, gapMode, colSpan }) {
  const [open, setOpen] = useState(false);
  if (!skills.length) return null;
  const totalFindings = skills.reduce((s, k) => s + (k.findingCount ?? 0), 0);
  const totalExpLow   = skills.reduce((s, k) => s + (k.exposureLow ?? 0), 0);
  const totalExpHigh  = skills.reduce((s, k) => s + (k.exposureHigh ?? 0), 0);
  const names = skills.map(k => k.skillLabel).join(", ");

  const row = (
    <>
      <tr
        onClick={() => setOpen(v => !v)}
        className="border-b border-dashed border-gray-100 cursor-pointer hover:bg-gray-50 opacity-70"
      >
        <td className="px-5 py-2.5">
          <div className="flex items-center gap-2">
            <ChevronRight className={`w-3.5 h-3.5 text-gray-300 transition-transform ${open ? "rotate-90" : ""}`} />
            <div>
              <div className="text-xs font-medium text-gray-500">
                Other Low-Volume Areas ({skills.length})
              </div>
              <div className="text-[10px] text-gray-400">{names}</div>
            </div>
          </div>
        </td>
        <td className="px-3 py-2.5 text-right">
          <span className="text-xs text-gray-400">{skills.reduce((s,k) => s+(k.requiredFte??0),0).toFixed(1)}</span>
          <span className="text-xs text-gray-300 ml-1">FTE</span>
        </td>
        {gapMode && (
          <>
            <td /><td /><td /><td />
          </>
        )}
        <td className="px-3 py-2.5 text-right">
          <div className="text-xs text-gray-400">{fmtM(totalExpLow)}–{fmtM(totalExpHigh)}</div>
        </td>
        <td className="px-3 py-2.5 text-center">
          <span className="text-[10px] text-gray-400">—</span>
        </td>
        <td className="px-3 py-2.5 text-center">
          <span className="text-xs text-gray-400 tabular-nums">{totalFindings}</span>
        </td>
        <td />
      </tr>
      {open && skills.map(s => (
        <tr key={s.skillArea} className="border-b border-gray-50 bg-gray-50/50">
          <td className="px-5 py-2 pl-12">
            <div className="text-xs text-gray-600">{s.skillLabel}</div>
            <div className="text-[10px] text-gray-400">{s.findingCount} finding{s.findingCount !== 1 ? "s" : ""}</div>
          </td>
          <td className="px-3 py-2 text-right text-xs text-gray-500">{(s.requiredFte??0).toFixed(1)} FTE</td>
          {gapMode && <><td /><td /><td /><td /></>}
          <td className="px-3 py-2 text-right text-xs text-gray-400">{fmtM(s.exposureLow)}–{fmtM(s.exposureHigh)}</td>
          <td /><td /><td />
        </tr>
      ))}
    </>
  );

  if (colSpan) return row;
  return null;
}

export default function WieSkillTable({ skills, mode, onSelectSkill, selectedSkill, horizonDays, apiCall, canManage, onOverrideSaved }) {
  const [showEmpty, setShowEmpty] = useState(false);
  if (!skills?.length) return null;

  const gapMode = mode === "GAP_ANALYSIS";
  const colSpan = gapMode ? 10 : 6; // total columns in desktop table (gap adds: Available, Coverage, Gap, Utilization)

  // Partition skills into display tiers
  const emptySkills    = skills.filter(s => (s.findingCount ?? 0) === 0 && (s.exposureHigh ?? 0) === 0);
  const lowVolSkills   = skills.filter(s => (s.findingCount ?? 0) > 0 && (s.findingCount ?? 0) < 3);
  const mainSkills     = skills.filter(s => (s.findingCount ?? 0) >= 3);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-4">
      {/* Table header */}
      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-sm font-bold text-gray-900">Skill Area Breakdown</h3>
        <div className="flex items-center gap-3">
          {emptySkills.length > 0 && (
            <button
              onClick={() => setShowEmpty(v => !v)}
              className="text-[10px] text-gray-400 hover:text-gray-600 underline"
            >
              {showEmpty ? "Hide" : "Show"} {emptySkills.length} empty area{emptySkills.length !== 1 ? "s" : ""}
            </button>
          )}
          <span className="text-xs text-gray-400">{mainSkills.length} active skill areas</span>
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="text-left text-xs font-semibold text-gray-500 px-5 py-2.5 w-44">Skill Area</th>
              <th className="text-right text-xs font-semibold text-gray-500 px-3 py-2.5">Required</th>
              {gapMode && (
                <>
                  <th className="text-right text-xs font-semibold text-gray-500 px-3 py-2.5">Available</th>
                  <th className="text-center text-xs font-semibold text-gray-500 px-3 py-2.5 min-w-[100px]">Coverage</th>
                  <th className="text-left  text-xs font-semibold text-gray-500 px-3 py-2.5">Gap</th>
                  <th className="text-center text-xs font-semibold text-gray-500 px-3 py-2.5" title="Required ÷ Available — Critical ≥90% · Healthy 70–90% · Overstaffed &lt;70%">
                    <span className="inline-flex items-center gap-1"><Activity className="w-3 h-3" />Util</span>
                  </th>
                </>
              )}
              <th className="text-right text-xs font-semibold text-gray-500 px-3 py-2.5">Exposure</th>
              <th className="text-center text-xs font-semibold text-gray-500 px-3 py-2.5">Confidence</th>
              <th className="text-center text-xs font-semibold text-gray-500 px-3 py-2.5">Findings</th>
              <th className="px-3 py-2.5 w-8" />
            </tr>
          </thead>
          <tbody>
            {mainSkills.map((skill) => {
              const conf = CONF[skill.confidence] || CONF.MEDIUM;
              const isSelected = selectedSkill === skill.skillArea;

              // Derive utilization from the displayed (rounded) FTE values to avoid math-doesn't-add-up confusion
            const displayUtil = skill.availableFte > 0 && skill.requiredFte > 0
              ? Math.round((skill.requiredFte / skill.availableFte) * 100)
              : 0;
            const displayUtilStatus = skill.availableFte <= 0 ? "Unconfigured"
              : displayUtil >= 90 ? "Critical"
              : displayUtil >= 70 ? "Healthy"
              : "Overstaffed";

            return (
                <React.Fragment key={skill.skillArea}>
                  <tr
                    onClick={() => onSelectSkill?.(isSelected ? null : skill.skillArea)}
                    className={`border-b border-gray-50 cursor-pointer transition-colors
                      ${isSelected
                        ? "bg-blue-50 hover:bg-blue-50"
                        : "hover:bg-gray-50"
                      }`}
                  >
                    <td className="px-5 py-3">
                      <div className="font-semibold text-gray-900 text-xs">{skill.skillLabel}</div>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <span className="text-xs text-gray-400">
                          {skill.findingCount} risks
                          {skill.duplicatesRemoved > 0 && (
                            <span className="text-gray-500 ml-1">({skill.duplicatesRemoved} merged)</span>
                          )}
                        </span>
                        {gapMode && skill.coveragePercent != null && skill.coveragePercent < 20 && skill.findingCount >= 5 && (
                          skill.requiredFte < 0.1 ? (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700"
                              title="Low-volume area with no assigned ownership. Risk is missed coverage, not workload.">
                              <AlertTriangle className="w-2.5 h-2.5" />
                              Specialist Dependency
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">
                              <AlertTriangle className="w-2.5 h-2.5" />
                              Critical Gap
                            </span>
                          )
                        )}
                      </div>
                      {/* Severity chips */}
                      {skill.severityBreakdown && skill.severityBreakdown.total > 0 && (
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          {skill.severityBreakdown.critical > 0 && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">
                              {skill.severityBreakdown.critical}C
                            </span>
                          )}
                          {skill.severityBreakdown.high > 0 && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">
                              {skill.severityBreakdown.high}H
                            </span>
                          )}
                          {skill.severityBreakdown.medium > 0 && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                              {skill.severityBreakdown.medium}M
                            </span>
                          )}
                          {skill.severityBreakdown.low > 0 && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                              {skill.severityBreakdown.low}L
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <span className="font-bold text-blue-700">{fmtFte(skill.requiredFte, skill.availableFte)}</span>
                      <span className="text-xs text-gray-400 ml-1">FTE</span>
                    </td>
                    {gapMode && (
                      <>
                        <td className="px-3 py-3 text-right">
                          <span className="font-medium text-gray-700">{fmtFte(skill.availableFte, skill.requiredFte)}</span>
                          <span className="text-xs text-gray-400 ml-1">FTE</span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <CoveragePill pct={skill.coveragePercent} />
                        </td>
                        <td className="px-3 py-3">
                          <GapBadge gap={skill.gapFte} />
                        </td>
                        <td className="px-3 py-3 text-center">
                          <UtilizationBadge status={displayUtilStatus} pct={displayUtil} />
                        </td>
                      </>
                    )}
                    <td className="px-3 py-3 text-right">
                      <div className="text-xs font-medium text-orange-700">
                        {fmtM(skill.exposureLow)}–{fmtM(skill.exposureHigh)}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded-full ${conf.bg} ${conf.text}`}>
                        <span className={`w-1 h-1 rounded-full ${conf.dot}`} />
                        {conf.label}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="text-xs text-gray-500 tabular-nums">{skill.findingCount}</span>
                      {skill.duplicatesRemoved > 0 && (
                        <div className="text-[10px] text-gray-500 tabular-nums">
                          <Layers className="w-2.5 h-2.5 inline mr-0.5" />{skill.duplicatesRemoved}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <ChevronRight
                        className={`w-4 h-4 transition-transform ${isSelected ? "rotate-90 text-blue-600" : "text-gray-300"}`}
                      />
                    </td>
                  </tr>

                  {/* Inline drilldown row — expands immediately below the clicked row */}
                  {isSelected && (
                    <DrilldownPanel
                      skill={skill}
                      colSpan={colSpan}
                      onClose={() => onSelectSkill?.(null)}
                      horizonDays={horizonDays}
                      apiCall={apiCall}
                      canManage={canManage}
                      onOverrideSaved={onOverrideSaved}
                    />
                  )}
                </React.Fragment>
              );
            })}
            {/* Low-volume skill areas grouped at the bottom */}
            <LowVolumeRow skills={lowVolSkills} gapMode={gapMode} colSpan={colSpan} />
            {/* Empty skill areas — shown only when toggle is on */}
            {showEmpty && emptySkills.map(skill => (
              <tr key={skill.skillArea} className="border-b border-gray-50 opacity-40">
                <td className="px-5 py-2.5">
                  <div className="text-xs text-gray-400 italic">{skill.skillLabel}</div>
                  <div className="text-[10px] text-gray-300">No open findings</div>
                </td>
                <td className="px-3 py-2.5 text-right text-xs text-gray-300">—</td>
                {gapMode && <><td /><td /><td /><td /></>}
                <td className="px-3 py-2.5 text-right text-xs text-gray-300">—</td>
                <td /><td className="px-3 py-2.5 text-center text-xs text-gray-300">0</td><td />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-gray-50">
        {mainSkills.map((skill) => {
          const conf = CONF[skill.confidence] || CONF.MEDIUM;
          const isSelected = selectedSkill === skill.skillArea;

          return (
            <div key={skill.skillArea}>
              <div
                onClick={() => onSelectSkill?.(isSelected ? null : skill.skillArea)}
                className={`p-4 cursor-pointer transition-colors ${isSelected ? "bg-blue-50" : "hover:bg-gray-50"}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{skill.skillLabel}</div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-400">
                        {skill.findingCount} risks
                        {skill.duplicatesRemoved > 0 && (
                          <span className="text-gray-500 ml-1">({skill.duplicatesRemoved} merged)</span>
                        )}
                      </span>
                      {gapMode && skill.coveragePercent != null && skill.coveragePercent < 20 && skill.findingCount >= 5 && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">
                          <AlertTriangle className="w-2.5 h-2.5" />
                          Critical Gap
                        </span>
                      )}
                    </div>
                    {skill.severityBreakdown && skill.severityBreakdown.total > 0 && (
                      <div className="flex items-center gap-1 mt-1 flex-wrap">
                        {skill.severityBreakdown.critical > 0 && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">{skill.severityBreakdown.critical}C</span>
                        )}
                        {skill.severityBreakdown.high > 0 && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">{skill.severityBreakdown.high}H</span>
                        )}
                        {skill.severityBreakdown.medium > 0 && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{skill.severityBreakdown.medium}M</span>
                        )}
                        {skill.severityBreakdown.low > 0 && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{skill.severityBreakdown.low}L</span>
                        )}
                      </div>
                    )}
                  </div>
                  <ChevronRight className={`w-4 h-4 mt-0.5 flex-shrink-0 transition-transform ${isSelected ? "rotate-90 text-blue-600" : "text-gray-300"}`} />
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  <div>
                    <div className="text-xs text-gray-400">Required</div>
                    <div className="text-sm font-bold text-blue-700">{fmt(skill.requiredFte)} FTE</div>
                  </div>
                  {gapMode && skill.gapFte != null && (
                    <div>
                      <div className="text-xs text-gray-400">Gap</div>
                      <GapBadge gap={skill.gapFte} />
                    </div>
                  )}
                  <div>
                    <div className="text-xs text-gray-400">Exposure</div>
                    <div className="text-xs font-medium text-orange-700">{fmtM(skill.exposureLow)}–{fmtM(skill.exposureHigh)}</div>
                  </div>
                  <span className={`inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded-full ${conf.bg} ${conf.text}`}>
                    <span className={`w-1 h-1 rounded-full ${conf.dot}`} />
                    {conf.label} conf.
                  </span>
                </div>
                {gapMode && skill.coveragePercent != null && (
                  <div className="mt-2">
                    <CoveragePill pct={skill.coveragePercent} />
                  </div>
                )}
              </div>

              {/* Inline drilldown for mobile — expands below the tapped card */}
              {isSelected && (
                <DrilldownPanel
                  skill={skill}
                  colSpan={null}
                  onClose={() => onSelectSkill?.(null)}
                  horizonDays={horizonDays}
                  apiCall={apiCall}
                  canManage={canManage}
                  onOverrideSaved={onOverrideSaved}
                />
              )}
            </div>
          );
        })}
        {/* Low-volume mobile summary */}
        {lowVolSkills.length > 0 && (
          <div className="p-4 opacity-60">
            <div className="text-xs text-gray-500 font-medium">
              + {lowVolSkills.length} low-volume area{lowVolSkills.length !== 1 ? "s" : ""} ({lowVolSkills.map(s => s.skillLabel).join(", ")})
            </div>
            <div className="text-[10px] text-gray-400">
              {lowVolSkills.reduce((s,k) => s+(k.findingCount??0),0)} findings total · below 3-finding threshold for individual display
            </div>
          </div>
        )}
      </div>

      {/* Footer note */}
      <div className="px-5 py-2.5 border-t border-gray-100 bg-gray-50">
        <p className="text-xs text-gray-400">
          FTE calculations use industry-benchmark effort models (calibratable per skill area).
          Click any row to view risk drivers, formula details, and calibration controls.
          <span className="text-red-500 font-medium"> ⚠ Critical Gap</span> = coverage 0–19% with 5+ findings and required FTE ≥ 0.1 (significant workload, no coverage).
          <span className="text-orange-500 font-medium"> ⚠ Specialist Dependency</span> = coverage 0–19% with required FTE &lt;0.1 (minimal workload but no named owner — ownership risk, not volume risk).
        </p>
      </div>
    </div>
  );
}
